import React, { useCallback, useEffect, useRef } from "react";
import {
  Box,
  Drawer,
  IconButton,
  useTheme,
  type Theme,
} from "@mui/material";
import {
  CalendarTodayOutlined,
  Close,
  DownloadOutlined,
  ErrorOutline,
  PersonOutline,
  SendOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { observer } from "mobx-react";
import { FilterValue } from "../../../models/Filters/FilterValue";
import {
  getPodOrderRowKey,
  PodOrderPrintingFileResponce,
  type PodOrderPrintOutwardConfirmPayload,
} from "../../../models/PodOrder/PodOrder";
import { useStores } from "../../../use-stores";
import { downloadFileFromUrl, fileNameFromUrl } from "../../../utils/urlHelper";
import AppButton from "../common/buttons/AppButton";
import CustomTypography from "../common/form/CustomTypography";
import FormTextField from "../common/form/FormTextField";
import AppSearchDropdownMulti from "../common/filter/AppSearchDropdownMulti";
import DatePicker2 from "../common/textFields/DatePicker2";
import AddEditMockups from "../../pages/orders/AddEditMockups";

const DRAWER_Z_OFFSET = 2000;


const getFileBadge = (theme: Theme, extension: string) => {
  const badgeColors: Record<string, { bg: string; color: string }> = {
    AI: { bg: theme.palette.customColors.lightOrange, color: theme.palette.orange.dark },
    CDR: { bg: theme.palette.customColors.successGreenLightBorder, color: theme.palette.customColors.successGreen },
    EPS: { bg: theme.palette.customColors.blueLightBackground, color: theme.palette.customColors.info },
    PDF: { bg: theme.palette.blushPink.main, color: theme.palette.customColors.errorRed },
    PNG: { bg: theme.palette.customColors.lightYellow, color: theme.palette.orange.dark },
  };
  return (
    badgeColors[extension.toUpperCase()] ?? {
      bg: theme.palette.customColors.lightGreyColor,
      color: theme.palette.customColors.grayText,
    }
  );
};

const getFileExtension = (file: PodOrderPrintingFileResponce) => {
  const name = fileNameFromUrl(file.image_url);
  const parts = name.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : "FILE";
};

export interface PodOrderPrintOutwardDrawerProps {
  open: boolean;
  onClose: () => void;
  submitting?: boolean;
  onConfirm: (payload: PodOrderPrintOutwardConfirmPayload) => void;
}

const PodOrderPrintOutwardDrawer: React.FC<PodOrderPrintOutwardDrawerProps> =
  ({ open, onClose, submitting = false, onConfirm }) => {
    const theme = useTheme();
    const { shipmentStore, podOrderStore } = useStores();

    const handleDownloadFile = (url: string) => {
      void downloadFileFromUrl(url, fileNameFromUrl(url) || "download");
    };

    const lines = [...podOrderStore.printOutwardLines].sort(
      (a, b) =>
        a.order_id - b.order_id ||
        a.product_id - b.product_id ||
        (a.stock_issue_request_id ?? 0) - (b.stock_issue_request_id ?? 0),
    );
    const printingFiles = podOrderStore.printOutwardPrintingFiles.map(file => {
      const line = lines.find(
        l => l.order_id === file.order_id && l.product_id === file.product_id,
      );
      return {
        id: file.id,
        name: fileNameFromUrl(file.image_url) || `Printing file #${file.id}`,
        productLabel:
          line?.product_name?.trim() || `Product #${file.product_id}`,
        extension: getFileExtension(file),
        downloadUrl: file.image_url,
      };
    });
    const totalQuantity = lines.reduce((sum, line) => sum + line.quantity, 0);
    const printerOptions = shipmentStore.deliveryPartners.map(item => ({
      id: String(item.delivery_partner_id),
      value: item.delivery_partner_name ?? "",
    }));

    const selectedPrinter = podOrderStore.printOutwardPrinter?.id
      ? [
        {
          id: String(podOrderStore.printOutwardPrinter.id),
          value: String(podOrderStore.printOutwardPrinter.value),
        },
      ]
      : [];

    const printerSearchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
      null,
    );

    useEffect(() => {
      if (open) return;
      if (printerSearchTimerRef.current) {
        clearTimeout(printerSearchTimerRef.current);
        printerSearchTimerRef.current = null;
      }
    }, [open]);

    const handlePrinterSearch = useCallback(
      (searchText?: string) => {
        if (printerSearchTimerRef.current) {
          clearTimeout(printerSearchTimerRef.current);
          printerSearchTimerRef.current = null;
        }

        const trimmed = (searchText ?? "").trim();
        if (trimmed.length < 3) {
          return;
        }

        printerSearchTimerRef.current = setTimeout(() => {
          shipmentStore.getDeliveryPartner(trimmed, false, true);
          printerSearchTimerRef.current = null;
        }, 500);
      },
      [shipmentStore],
    );

    const handleConfirm = () => {
      if (!podOrderStore.validatePrintOutwardForm()) return;
      onConfirm(podOrderStore.getPrintOutwardConfirmPayload());
    };

    return (
      <Drawer
        anchor="right"
        open={open}
        onClose={submitting ? undefined : onClose}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableRestoreFocus: true,
          sx: { zIndex: t => t.zIndex.drawer + DRAWER_Z_OFFSET },
          slotProps: {
            backdrop: { sx: { zIndex: t => t.zIndex.drawer + DRAWER_Z_OFFSET - 1 } },
          },
        }}
        PaperProps={{
          sx: {
            width: { xl: 580, lg: 540, md: 520, sm: "100vw", xs: "100vw" },
            maxWidth: "100vw",
            display: "flex",
            flexDirection: "column",
            zIndex: t => t.zIndex.drawer + DRAWER_Z_OFFSET,
            boxShadow: "-4px 0 24px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            position: "relative",
          }}
        >
          <IconButton
            onClick={onClose}
            disabled={submitting}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: theme.palette.grey[100],
              "&:hover": { backgroundColor: theme.palette.grey[200] },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pr: 5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: "12px",
                backgroundColor: theme.palette.primary.main,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SendOutlined
                sx={{ color: theme.palette.primary.contrastText, fontSize: 22 }}
              />
            </Box>
            <Box>
              <CustomTypography variant="heading5">Send To Printer</CustomTypography>
              <CustomTypography variant="body2" color="text.secondary">
                Configure workflow action for {lines.length} selected {lines.length === 1 ? "item" : "items"}
              </CustomTypography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2.5 }}>
          <>
            {podOrderStore.printOutwardError ? (
              <Box
                role="alert"
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 1.5,
                  px: 1.5,
                  py: 1.25,
                  borderRadius: "10px",
                  border: "1px solid",
                  borderColor: theme.palette.customColors.errorRed,
                  backgroundColor: `${theme.palette.error.main}10`,
                }}
              >
                <ErrorOutline
                  sx={{
                    fontSize: 20,
                    color: theme.palette.customColors.errorRed,
                    flexShrink: 0,
                    mt: 0.1,
                  }}
                />
                <CustomTypography
                  variant="body2"
                  sx={{ color: theme.palette.customColors.errorRed, lineHeight: 1.45 }}
                >
                  {podOrderStore.printOutwardError}
                </CustomTypography>
              </Box>
            ) : null}

            <Box
              sx={{
                border: "1px solid",
                borderColor: theme.palette.customColors.lightGray,
                borderRadius: "12px",
                backgroundColor: theme.palette.customColors.lightGrayBackground,
                maxHeight: 280,
                overflowY: "auto",
                p: 1.25,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <CustomTypography variant="heading7">Selected Products</CustomTypography>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    px: 1.25,
                    py: 0.35,
                    borderRadius: "999px",
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: theme.palette.customColors.lightPurple,
                    color: theme.palette.customColors.purpleMain,
                  }}
                >
                  {lines.length} items
                </Box>
              </Box>
              {lines.map(line => {
                const productName =
                  line.product_name?.trim() || `Product #${line.product_id}`;
                const variant = line.sku?.trim() || "—";
                return (
                  <Box
                    key={getPodOrderRowKey({
                      order_id: line.order_id,
                      product_id: line.product_id,
                      stock_issue_request_id: line.stock_issue_request_id ?? null,
                    })}
                    sx={{
                      px: 2,
                      py: 1.5,
                      border: "1px solid",
                      borderColor: theme.palette.customColors.lightGray,
                      borderRadius: "12px",
                      backgroundColor: theme.palette.customColors.white,
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <CustomTypography
                        variant="body2"
                        sx={{ fontWeight: 600, lineHeight: 1.45 }}
                      >
                        <Box component="span" sx={{ color: theme.palette.customColors.purpleMain, fontWeight: 700 }}>
                          ORD-{line.order_id}
                        </Box>
                        <Box component="span" sx={{ mx: 0.75 }}>
                        • {productName}
                        </Box>
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35, lineHeight: 1.45 }}
                      >
                        • {variant}
                        {line.stock_issue_request_id != null ? (
                          <>
                            <Box component="span" sx={{ mx: 0.75 }}>•</Box>
                            SIR {line.stock_issue_request_id}
                          </>
                        ) : null}
                      </CustomTypography>
                      <CustomTypography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.35 }}
                      >
                        • ORDER QTY: {line.quantity}
                      </CustomTypography>
                    </Box>
                    <AppButton
                      variant="grayscale"
                      label="Mockup"
                      fontSize="0.72rem"
                      startIcon={<VisibilityOutlined sx={{ fontSize: 16 }} />}
                      onClick={() => podOrderStore.openPrintOutwardMockupPreview(line)}
                      customStyles={{
                        minHeight: 28,
                        height: 28,
                        py: 0,
                        px: 1.5,
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                );
              })}
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                <PersonOutline sx={{ fontSize: 18, color: theme.palette.customColors.greenDark }} />
                <CustomTypography variant="heading7">Select Printer <span style={{ color: theme.palette.customColors.errorRed }}>*</span></CustomTypography>
              </Box>
              <AppSearchDropdownMulti
                isMultiSelect={false}
                title="Search printer"
                local={false}
                borderColor={theme.palette.customColors.greyDarkborder ?? theme.palette.customColors.switchTrackGray}
                borderRadius="12px"
                inputHeight="40px"
                list={printerOptions}
                value={selectedPrinter}
                handleSearch={handlePrinterSearch}
                handleSelect={(selectedVendor: FilterValue[]) => {
                  if (
                    selectedVendor.length > 0 &&
                    selectedVendor[0].id !== undefined
                  ) {
                    podOrderStore.setPrintOutwardPrinter({
                      id: String(selectedVendor[0].id),
                      value: String(selectedVendor[0].value),
                    });
                  } else {
                    podOrderStore.setPrintOutwardPrinter(null);
                  }
                }}
                error={podOrderStore.printOutwardPrinterError}
                errorMessage="Please select a printer"
              />
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 1 }}>
                <CalendarTodayOutlined sx={{ fontSize: 18, color: theme.palette.customColors.purpleMain }} />
                <CustomTypography variant="heading7">Print Due Date <span style={{ color: theme.palette.customColors.errorRed }}>*</span></CustomTypography>
              </Box>
              <DatePicker2
                label=""
                type="date"
                value={podOrderStore.printOutwardDueDate}
                onChange={date => podOrderStore.setPrintOutwardDueDate(date)}
                require
                error={podOrderStore.printOutwardDueDateError}
                zIndex={theme.zIndex.drawer + DRAWER_Z_OFFSET + 1}
                borderColor={`0.1px solid ${theme.palette.customColors.greyDarkborder ?? theme.palette.customColors.switchTrackGray}`}
                minDate={dayjs().startOf("day")}
              />
              {podOrderStore.printOutwardDueDateError && (
                <CustomTypography variant="body3" color="error.main" sx={{ mt: 0.75 }}>
                  Please choose a print due date
                </CustomTypography>
              )}
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <CustomTypography variant="heading7" sx={{ mb: 1 }}>
                Instructions for Printer
              </CustomTypography>
              <FormTextField
                key="pod-print-outward-notes"
                id="pod-print-outward-notes"
                placeholder="Add any special instructions or notes..."
                multiline
                rows={2}
                value={podOrderStore.printOutwardNotes}
                marginBottom={0}
                onChange={(_, value) => podOrderStore.setPrintOutwardNotes(value ?? "")}
                borderColor={theme.palette.customColors.greyDarkborder ?? theme.palette.customColors.switchTrackGray}
                backgroundColor={theme.palette.customColors.white}
                sx={{
                  mb: 0,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    backgroundColor: theme.palette.customColors.white,
                  },
                }}
              />
            </Box>

            <Box sx={{ mt: 2.5 }}>
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: theme.palette.customColors.lightGray,
                  borderRadius: "12px",
                  backgroundColor: theme.palette.customColors.lightGrayBackground,
                  maxHeight: 260,
                  overflowY: "auto",
                  p: 1.25,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 0.5,
                  }}
                >
                  <CustomTypography variant="heading7">Printing Files</CustomTypography>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      px: 1.25,
                      py: 0.35,
                      borderRadius: "999px",
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: theme.palette.customColors.successGreenLightBorder,
                      color: theme.palette.customColors.successGreen,
                    }}
                  >
                    {printingFiles.length} Files
                  </Box>
                </Box>
                {podOrderStore.printOutwardFilesLoading && (
                  <CustomTypography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                    Loading printing files…
                  </CustomTypography>
                )}
                {!podOrderStore.printOutwardFilesLoading &&
                  printingFiles.length === 0 && (
                    <CustomTypography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
                      No printing files found.
                    </CustomTypography>
                  )}
                {printingFiles.map((file, index) => {
                  const badge = getFileBadge(theme, file.extension);
                  return (
                    <Box
                      key={`${file.id}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1.5,
                        py: 1.25,
                        border: "1px solid",
                        borderColor: theme.palette.customColors.lightGray,
                        borderRadius: "12px",
                        backgroundColor: theme.palette.customColors.white,
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          backgroundColor: badge.bg,
                          color: badge.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {file.extension}
                      </Box>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <CustomTypography
                          variant="body2"
                          fontWeight={700}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </CustomTypography>
                        <CustomTypography variant="body3" color="text.secondary" sx={{ mt: 0.25 }}>
                          {file.productLabel}
                        </CustomTypography>
                      </Box>
                      <AppButton
                        variant="grayscale"
                        label="Download"
                        startIcon={<DownloadOutlined />}
                        disabled={!file.downloadUrl}
                        onClick={() =>
                          handleDownloadFile(
                            file.downloadUrl ?? "",
                          )
                        }
                        customStyles={{ minWidth: 90, height: 30, minHeight: 30, px: 1.25 }}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>

            <Box
              sx={{
                mt: 2.5,
                px: 2,
                py: 1.5,
                borderRadius: "12px",
                backgroundColor: theme.palette.customColors.lightPurple,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  Total Items:
                </CustomTypography>
                <CustomTypography variant="body2" fontWeight={600}>
                  {lines.length}
                </CustomTypography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <CustomTypography variant="body2" fontWeight={600}>
                  Total Quantity:
                </CustomTypography>
                <CustomTypography variant="body2" fontWeight={600}>
                  {totalQuantity}
                </CustomTypography>
              </Box>
            </Box>
          </>
        </Box>

        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
          }}
        >
          <AppButton
            variant="secondary"
            label="Cancel"
            disabled={submitting}
            onClick={onClose}
            customStyles={{ minWidth: 120 }}
          />
          <AppButton
            variant="primary"
            label={submitting ? "Updating…" : "Confirm Action"}
            disabled={submitting}
            startIcon={<SendOutlined />}
            onClick={handleConfirm}
            customStyles={{ minWidth: 160 }}
          />
        </Box>

        {podOrderStore.printInwardMockupPreviewOpen &&
          podOrderStore.printInwardMockupPreviewProduct && (
            <AddEditMockups
              viewOnly
              dialogZIndex={theme.zIndex.drawer + DRAWER_Z_OFFSET + 500}
              toggleMockupsPopup={() => podOrderStore.closePrintInwardMockupPreview()}
              product={podOrderStore.printInwardMockupPreviewProduct}
              mockups={podOrderStore.printInwardMockupPreviewMockups}
              isMainMockupImageLoading={podOrderStore.printInwardMockupPreviewLoading}
              canDeleteMockup={false}
              archived={1}
            />
          )}
      </Drawer>
    );
  };

export default observer(PodOrderPrintOutwardDrawer);
